import { ApiVwRx, ApiVwTx, ApiVw, RotateFlip } from '@/types/api'
import { IVW } from '@/types/model'
import { deepCopy } from '@/utils'

export const VW_MAX_COLUMNS = 5
export const VW_MAX_ROWS = 5

export interface point_t {
  x: number
  y: number
}

export interface geometry_t extends geometry_size_t, point_t { }

export interface geometry_size_t {
  width: number
  height: number
}

export interface screen_style_t {
  /** count from 1 */
  displayIndex: number
  style: string
  clickable: boolean
  /** CSS class for Intriglyph icon font */
  hasBezelSet: boolean
  rxSelected: boolean
  /** 0 means none */
  osd: number
}

export interface tx_area_style_t {
  mac: string
  name: string
  style: string
  geometry: geometry_t
  rotateFlipClass: string
}

export interface vw_pos {
    /** Nth column. Counted from 0 */
    column: number
    /** Nth row. Counted from 0 */
    row: number
}

export interface vw_color_t {
  /** background-color */
  bg: string
  /** border-color */
  bd: string
}

/** Create rxList by layout if rxList.length is not matched with columns * rows. */
export function vwValidateRxTxList (vw: ApiVw): ApiVw {
  let c = vw.layout.columns
  let r = vw.layout.rows
  if (vw.rxList.length !== c * r) {
    if (c <= 0) { console.error('[To Developer] Cannot <= 0; force set to 1'); c = 1 }
    if (r <= 0) { console.error('[To Developer] Cannot <= 0; force set to 1'); r = 1 }
    vw.rxList = Array(c * r).fill(0).map((_, i) => ({
      mac: '',
      displayIndex: i + 1,
      rotateFlip: RotateFlip.normal,
      osd: 0,
      bezel: {
        ow: 0,
        oh: 0,
        iw: 0,
        ih: 0,
      }
    }))
    vw.txList = []
  }
  return vw
}

export function integer2VwTxColor (index: number): vw_color_t {
  const i = ~~(index % 8)
  if (i < 0) {
    console.error('[To Developer] This should not be happened. For an invalid TX, it will be assigned with such a disgusting color.')
    return { bg: '#ff00ea', bd: '#ff00ea'}
  }  // invalid
  return [
    {bg: '#86C6F5', bd: '#3C91CE', },
    {bg: '#AFDD1B', bd: '#8AA62D', },
    {bg: '#F0AA5A', bd: '#BF702A', },
    {bg: '#84DBCF', bd: '#4BA69A', },
    {bg: '#C091E2', bd: '#703B96', },
    {bg: '#FFED5D', bd: '#BBA42E', },
    {bg: '#96ACEC', bd: '#435FB0', },
    {bg: '#F16363', bd: '#B22F2F', },
  ][i]
}

/** Get color conbination for a specific TX (and its Rect Area) in VW */
export function getVwTxColor (txMac: string, vw: IVW): vw_color_t {
  const i = vw.txList.findIndex(x => x.mac === txMac)
  return integer2VwTxColor(i)
}

export function getVwTxColorWhenTxInVw (txMac: string, vw: IVW): null | vw_color_t {
  const i = vw.txList.findIndex(x => x.mac === txMac)
  return i === -1 ? null : integer2VwTxColor(i)
}

export function getVwPosFromRx (rx: ApiVwRx, vw: IVW): vw_pos {
  return getVwPosFromRxDisplayIndex(vw, rx.displayIndex)
}

export function getVwPosFromRxDisplayIndex (vw: IVW, rxDisplayIndex: number): vw_pos {
  const columns = vw.layout.columns
  const rows = vw.layout.rows
  // following const are counted from 0.
  const rxIndex = rxDisplayIndex - 1
  const column = rxIndex % columns
  const row = (rxIndex - column) / columns
  return { row, column }
}


export function getVwTxFromVwRx (rx: ApiVwRx, vw: IVW): ApiVwTx | null {
  const rxPos = getVwPosFromRx(rx, vw)
  for (const tx of vw.txList) {
    if (
      rxPos.row >= tx.row &&
      rxPos.row < tx.row + tx.rows &&
      rxPos.column >= tx.column &&
      rxPos.column < tx.column + tx.columns) { return tx }
  }
  return null
}

export function getVwScreenColor (rx: ApiVwRx, vw: IVW): vw_color_t | null {
  if (!rx.mac) { return null }
  const tx = getVwTxFromVwRx(rx, vw)
  if (!tx) {return null}
  if (!tx.mac || !rx.mac) { return {bg: '#999999', bd: '#555555'} }
  return getVwTxColor(tx.mac, vw)
}

export function vwTxOverlapped (m: ApiVwTx, n: ApiVwTx): boolean {
  for (let c = n.column; c < n.column + n.columns; c++) {
    for (let r = n.row; r < n.row + n.rows; r++) {
      if (
        (c >= m.column) &&
        (c < m.column + m.columns) &&
        (r >= m.row) &&
        (r < m.row + m.rows)
      ) {
        return true
      }
    }
  }
  return false
}

export function vwTxOutOfLayout (vw: IVW, tx: ApiVwTx) {
  return (
    (tx.column < 0) ||
    (tx.row < 0) ||
    (tx.column + tx.columns > vw.layout.columns) ||
    (tx.row + tx.rows > vw.layout.rows)
  )
}

export function vwTxSizeInvalid (vw: IVW, tx: ApiVwTx) {
  return (
    (tx.columns <= 0) ||
    (tx.rows <= 0)
  )
}

// ======================================================
// Move & Resize TX
// ======================================================

export type four_ways_direction_t = 'up' | 'down' | 'left' | 'right'

/** Will mutate vw directly */
export function vwMoveTxToward (vw: IVW, txMac: string, direction: four_ways_direction_t) {
  const origThisTx = vw.txList.find(x => x.mac === txMac)
  if (!origThisTx) { console.error('[To Developer] This should not happened.'); return false }
  const otherTxs = vw.txList.filter(x => x.mac !== txMac)
  const newTx = deepCopy(origThisTx)
  switch (direction) {
    case 'up': newTx.row -= 1; break
    case 'down': newTx.row += 1; break
    case 'left': newTx.column -= 1; break
    case 'right': newTx.column += 1; break
  }
  if (vwTxOutOfLayout(vw, newTx)) { return false }
  for (const tx of otherTxs) {
    if (vwTxOverlapped(newTx, tx)) {
      return false
    }
  }
  Object.assign(origThisTx, newTx)
  return true
}

/** Will mutate vw directly */
export function vwResizeTxToward (vw: IVW, txMac: string, direction: four_ways_direction_t) {
  const origThisTx = vw.txList.find(x => x.mac === txMac)
  if (!origThisTx) { console.error('[To Developer] This should not happened.'); return false }
  const otherTxs = vw.txList.filter(x => x.mac !== txMac)
  const newTx = deepCopy(origThisTx)
  switch (direction) {
    case 'up': newTx.rows -= 1; break
    case 'down': newTx.rows += 1; break
    case 'left': newTx.columns -= 1; break
    case 'right': newTx.columns += 1; break
  }
  if (vwTxOutOfLayout(vw, newTx) || vwTxSizeInvalid(vw, newTx)) {return false}
  for (const tx of otherTxs) {
    if (vwTxOverlapped(newTx, tx)) {
      return false
    }
  }
  Object.assign(origThisTx, newTx)
  return true
}

/** Will mutate vw directly */
export function vwMoveTxTo (vw: IVW, txMac: string, moveToRxDisplayIndex: number): boolean {
  const origThisTx = vw.txList.find(x => x.mac === txMac)
  if (!origThisTx) { console.error('[To Developer] This should not happened.'); return false }
  const otherTxs = vw.txList.filter(x => x.mac !== txMac)
  const newPos = getVwPosFromRxDisplayIndex(vw, moveToRxDisplayIndex)
  const newTx = deepCopy(origThisTx)
  newTx.column = newPos.column
  newTx.row = newPos.row
  if (vwTxOutOfLayout(vw, newTx)) { return false }
  for (const tx of otherTxs) {
    if (vwTxOverlapped(newTx, tx)) {
      return false
    }
  }
  Object.assign(origThisTx, newTx)
  return true
}
/** Will mutate vw directly */
export function vwResizeTxTo (vw: IVW, txMac: string, resizeToRxDisplayIndex: number) {
  const origThisTx = vw.txList.find(x => x.mac === txMac)
  if (!origThisTx) { console.error('[To Developer] This should not happened.'); return false }
  const otherTxs = vw.txList.filter(x => x.mac !== txMac)
  const newPos = getVwPosFromRxDisplayIndex(vw, resizeToRxDisplayIndex)  // mouse dragged to this pos
  const newTx = deepCopy(origThisTx)
  newTx.columns = newPos.column - origThisTx.column + 1
  newTx.rows = newPos.row - origThisTx.row + 1
  if (vwTxSizeInvalid(vw, newTx)) {return false}
  for (const tx of otherTxs) {
    if (vwTxOverlapped(newTx, tx)) {
      return false
    }
  }
  Object.assign(origThisTx, newTx)
  return true
}

// ======================================================
// Bezel
// ======================================================
export function vwRxHasBezelSet (rx: ApiVwRx): boolean {
  return (rx.bezel.ih !== 0 && rx.bezel.oh !== 0) || (rx.bezel.iw !== 0 && rx.bezel.ow !== 0)
}
